import type { QueryClient, QueryKey } from '@tanstack/react-query';

import { candidateJobQueryKeys } from '@/modules/candidates/queries/queryKeys';
import { refreshChat } from '@/modules/hiring-manager/chat/refreshChat';
import { jobsQueryKeys } from '@/modules/jobs/queries/queryKeys';
import { messagingQueryKeys } from '@/modules/messaging/queries/queryKeys';
import type {
  EntityChangePayload,
  NotificationEntityAction,
} from '@/modules/notifications/types';

/**
 * Refetches what a server notification says changed. Takes the client from
 * context so portal stories, which run their own client, see the refresh too.
 */
export const invalidateEntityQueries = (
  queryClient: QueryClient,
  entityAction: NotificationEntityAction,
  entityId?: string,
) => {
  const invalidate = (queryKey: QueryKey) =>
    void queryClient.invalidateQueries({ queryKey });

  switch (entityAction) {
    case 'JobCreated':
    case 'SourcingStarted':
    case 'SourcingFailed':
    case 'JobImportSuccess':
    case 'JobImportFailed':
      invalidate(jobsQueryKeys.all);
      break;
    case 'SourcingFinished':
      invalidate(jobsQueryKeys.all);
      invalidate(candidateJobQueryKeys.all);
      break;
    case 'ManualUploadFinished':
    case 'ManualUploadFailed':
      invalidate(
        entityId
          ? candidateJobQueryKeys.lists(entityId)
          : candidateJobQueryKeys.all,
      );
      break;
    case 'NewMessage':
    case 'MessageReviewRequired':
      invalidate(messagingQueryKeys.threads());
      invalidate(candidateJobQueryKeys.all);
      if (entityId) invalidate(messagingQueryKeys.thread(entityId));
      break;
    case 'OutreachGenerated':
      invalidate(messagingQueryKeys.messages(['Draft' as const]));
      invalidate(candidateJobQueryKeys.all);
      break;
    default:
      entityAction satisfies never;
  }
};

/** Refetches what a hub entity change (`ReceiveEntityChange`) says moved. */
export const invalidateEntityChange = (
  queryClient: QueryClient,
  change: EntityChangePayload,
) => {
  switch (change.entity) {
    case 'CandidateJobs':
      // The job's lists, counts and open candidate, plus the job's own stats.
      void queryClient.invalidateQueries({
        queryKey: candidateJobQueryKeys.lists(change.jobId),
      });
      void queryClient.invalidateQueries({
        queryKey: jobsQueryKeys.detail(change.jobId),
      });
      break;
    case 'Chat':
      refreshChat(queryClient, change.chatId);
      break;
    default:
      change satisfies never;
  }
};
